import React, { useContext, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import { endPoint } from "utils/config";

import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";
import AttachMoneyOutlinedIcon from "@material-ui/icons/AttachMoneyOutlined";
import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import ReceiptIcon from "@material-ui/icons/Receipt";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import StoreIcon from "@material-ui/icons/Store";
import IconButton from "@material-ui/core/IconButton";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import FilterDropdown from "designV2/components/FilterDropdown";
import Panel from "designV2/components/Panel";
import TrendChart from "designV2/components/TrendChart";
import StatusBadge from "designV2/components/StatusBadge";
import { MetricsSkeleton } from "designV2/components/Skeleton";
import { graphqlUrl } from "utils/config";
import { ThemeContext } from "index";
import "./dashboard.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

// One aggregated query replaces the seven the page used to fire. The old
// ones fetched raw rows and summed them in the browser, which silently
// capped every total at the API's default page size of 15.
const GET_DASHBOARD = gql`
  query getShopDashboard($range: ShopDashboardRange!) {
    getShopDashboard(range: $range) {
      range
      from
      to
      revenue {
        value
        previous
        changePercent
      }
      orders {
        value
        previous
        changePercent
      }
      averageOrderValue {
        value
        previous
        changePercent
      }
      series {
        date
        revenue
        orders
      }
      statusBreakdown {
        status
        count
      }
      actionRequiredCount
      topProducts {
        _id
        title
        photoUrl
        quantity
        revenue
      }
      lowStock {
        _id
        title
        photoUrl
        stock
        price
      }
      lowStockCount
      outOfStockCount
      productsCount
      reviews {
        count
        averageRating
      }
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const RANGE_OPTIONS = [
  { value: "TODAY", label: "Today" },
  { value: "LAST_7_DAYS", label: "Last 7 days" },
  { value: "LAST_30_DAYS", label: "Last 30 days" },
  { value: "LAST_90_DAYS", label: "Last 90 days" },
];

const RANGE_LABELS = RANGE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

// "vs the previous 7 days" reads better on a metric card than "vs previous".
const RANGE_COMPARISON = {
  TODAY: "vs yesterday",
  LAST_7_DAYS: "vs previous 7 days",
  LAST_30_DAYS: "vs previous 30 days",
  LAST_90_DAYS: "vs previous 90 days",
};

const STATUS_PRESENTATION = {
  PENDING: { label: "Awaiting payment", tone: "neutral" },
  PAID: { label: "New", tone: "info" },
  PREPARING: { label: "Preparing", tone: "warning" },
  PREPARED: { label: "Ready", tone: "accent" },
  SHIPPING: { label: "Shipping", tone: "info" },
  DELIVERED: { label: "Delivered", tone: "success" },
  REJECTED: { label: "Rejected", tone: "danger" },
  DELIVERY_NOT_ACCEPTED: { label: "Delivery refused", tone: "danger" },
  FINISHED_DUE_TO_NOT_PAYING: { label: "Unpaid", tone: "neutral" },
};

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const numberFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatMoney = (value) => `$${numberFormatter.format(value || 0)}`;

// Axis labels have to fit in a few pixels, so large amounts are abbreviated
// there while the tooltip keeps the exact figure.
const formatMoneyCompact = (value) => {
  const amount = value || 0;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}k`;
  return `$${Math.round(amount)}`;
};

const formatCount = (value) => new Intl.NumberFormat().format(value || 0);

// A change against a zero baseline is undefined rather than infinite, so the
// backend sends null and no delta is drawn.
const toDelta = (metric, comparison) => {
  if (!metric || metric.changePercent === null || metric.changePercent === undefined) {
    return undefined;
  }
  const rounded = Math.round(metric.changePercent * 10) / 10;
  if (rounded === 0) return { direction: "flat", label: `No change ${comparison}` };
  return {
    direction: rounded > 0 ? "up" : "down",
    label: `${Math.abs(rounded)}% ${comparison}`,
  };
};

const renderStars = (rating) => {
  const rounded = Math.round(rating || 0);
  return "★★★★★".slice(0, rounded) + "☆☆☆☆☆".slice(0, 5 - rounded);
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Dashboard = (props) => {
  const themeContext = useContext(ThemeContext);
  // routes.js registers the products route from localStorage, so the paths
  // here have to come from the same place or they will not resolve.
  const isRestaurant =
    window.localStorage.getItem("shopType") === "Restaurant" ||
    themeContext === "Restaurant";
  const productWord = isRestaurant ? "food" : "product";
  const productsWord = isRestaurant ? "Foods" : "Products";
  const productsPath = isRestaurant ? "/shopAdmin/foods" : "/shopAdmin/products";
  const addProductPath = isRestaurant
    ? "/shopAdmin/addFood"
    : "/shopAdmin/addProduct";

  const [range, setRange] = useState("LAST_30_DAYS");

  const { data, loading, error, refetch } = useQuery(GET_DASHBOARD, {
    variables: { range },
    fetchPolicy: "network-only",
  });

  // network-only clears `data` whenever the range changes, which would drop
  // the whole page back to a skeleton on every switch. Holding the last
  // successful payload lets the numbers stay put until the new ones land.
  const lastDashboard = useRef(null);
  if (data && data.getShopDashboard) {
    lastDashboard.current = data.getShopDashboard;
  }

  if (error) {
    return <ErrorPage />;
  }

  const dashboard = (data && data.getShopDashboard) || lastDashboard.current;
  const comparison = RANGE_COMPARISON[range];

  const chartPoints = dashboard
    ? dashboard.series.map((point) => ({
        date: point.date,
        value: point.revenue,
        meta: point.orders,
      }))
    : [];

  const statusRows = dashboard ? dashboard.statusBreakdown : [];
  const statusMax = statusRows.reduce(
    (max, row) => Math.max(max, row.count),
    0,
  );

  const header = (
    <PageHeader
      eyebrow={isRestaurant ? "Restaurant" : "Shop"}
      title="Dashboard"
      description={
        dashboard
          ? `${RANGE_LABELS[range]} · ${moment(dashboard.from).format("MMM D")} to ${moment(
              dashboard.to,
            ).format("MMM D, YYYY")}`
          : "How your shop is performing."
      }
      actions={
        <React.Fragment>
          <FilterDropdown
            label={RANGE_LABELS[range]}
            options={RANGE_OPTIONS}
            value={range}
            /* A period selector always holds a value; it is not narrowing
               anything, so it should not read as an applied filter. */
            active={false}
            align="right"
            onChange={(e) => setRange(e.target.value)}
          />
          <IconButton
            className="tableTopIconButton"
            size="small"
            onClick={() => refetch()}
            disabled={loading}
            title="Refresh"
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
          <PageActionButton
            variant="primary"
            icon={<AddIcon fontSize="small" />}
            label={`Add ${productWord}`}
            info={`New ${productWord} in your catalogue`}
            /* Dashboard is rendered through routes.js's generic route loop
               (render={() => <prop.component type={props.type} />}), which
               never passes history/match/location down — props.history was
               always undefined here, so this button threw on click instead
               of navigating. Every other "Add X" action in the app navigates
               the same way: a plain location change. */
            onClick={() => (window.location.href = endPoint + addProductPath)}
          />
        </React.Fragment>
      }
    />
  );

  // Also covers the brief window after a range change, and the case where
  // the query resolves with no payload at all.
  if (!dashboard) {
    return (
      <div className="page dashboardPage">
        <main className="main">
          {header}
          <MetricsSkeleton count={4} />
          <div className="dashboardGrid">
            <Panel title="Revenue">
              <div className="dsTrendEmpty">Loading…</div>
            </Panel>
            <Panel title="Order queue">
              <div className="dashboardEmptyLine">Loading…</div>
            </Panel>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="page dashboardPage">
      <main className="main">
        {header}

        {/* ATTENTION — only rendered when there is something to act on */}
        {(dashboard.actionRequiredCount > 0 ||
          dashboard.outOfStockCount > 0 ||
          dashboard.lowStockCount > 0) && (
          <div className="dashboardAlerts">
            {dashboard.actionRequiredCount > 0 && (
              <Link className="dashboardAlert attention" to="/shopAdmin/orders">
                <NotificationsActiveIcon />
                <span className="dashboardAlertText">
                  <strong>{dashboard.actionRequiredCount}</strong>{" "}
                  {dashboard.actionRequiredCount === 1 ? "order needs" : "orders need"}{" "}
                  your attention
                </span>
                <span className="dashboardAlertAction">Open →</span>
              </Link>
            )}
            {dashboard.outOfStockCount > 0 && (
              <Link className="dashboardAlert warning" to={productsPath}>
                <ReportProblemOutlinedIcon />
                <span className="dashboardAlertText">
                  <strong>{dashboard.outOfStockCount}</strong>{" "}
                  {dashboard.outOfStockCount === 1 ? "item is" : "items are"} out of
                  stock
                </span>
                <span className="dashboardAlertAction">Restock →</span>
              </Link>
            )}
            {dashboard.outOfStockCount === 0 && dashboard.lowStockCount > 0 && (
              <Link className="dashboardAlert warning" to={productsPath}>
                <ReportProblemOutlinedIcon />
                <span className="dashboardAlertText">
                  <strong>{dashboard.lowStockCount}</strong>{" "}
                  {dashboard.lowStockCount === 1 ? "item is" : "items are"} running low
                </span>
                <span className="dashboardAlertAction">Review →</span>
              </Link>
            )}
          </div>
        )}

        {/* METRICS */}
        <section className="stats">
          <MetricCard
            icon={<AttachMoneyOutlinedIcon />}
            title="Revenue"
            value={formatMoney(dashboard.revenue.value)}
            tone="success"
            delta={toDelta(dashboard.revenue, comparison)}
            hint={
              dashboard.revenue.previous
                ? undefined
                : `Nothing to compare ${comparison.replace("vs ", "against ")}`
            }
          />
          <MetricCard
            icon={<ShoppingCartIcon />}
            title="Orders"
            value={formatCount(dashboard.orders.value)}
            delta={toDelta(dashboard.orders, comparison)}
          />
          <MetricCard
            icon={<ReceiptIcon />}
            title="Average order"
            value={formatMoney(dashboard.averageOrderValue.value)}
            tone="info"
            delta={toDelta(dashboard.averageOrderValue, comparison)}
          />
          <MetricCard
            icon={<StarBorderIcon />}
            title="Reviews"
            value={formatCount(dashboard.reviews.count)}
            tone={dashboard.reviews.count > 0 ? "warning" : "default"}
            hint={
              dashboard.reviews.averageRating ? (
                <span className="dashboardRating">
                  <span className="dashboardRatingStars">
                    {renderStars(dashboard.reviews.averageRating)}
                  </span>
                  {dashboard.reviews.averageRating.toFixed(1)} average
                </span>
              ) : (
                "No ratings in this period"
              )
            }
          />
        </section>

        {/* CHART + ORDER QUEUE */}
        <div className="dashboardGrid">
          <Panel
            title="Revenue over time"
            subtitle={`Daily takings across the ${RANGE_LABELS[range].toLowerCase()}`}
          >
            <TrendChart
              data={chartPoints}
              formatValue={(value, isAxis) =>
                isAxis ? formatMoneyCompact(value) : formatMoney(value)
              }
              formatDate={(date) => moment(date).format("dddd, MMM D")}
              formatAxisDate={(date) => moment(date).format("MMM D")}
              metaLabel="orders"
              emptyMessage="No revenue recorded in this period"
            />
          </Panel>

          <Panel
            title="Order queue"
            subtitle={`${RANGE_LABELS[range]}`}
            footer={
              <Link className="dashboardPanelLink" to="/shopAdmin/orders">
                View all orders →
              </Link>
            }
          >
            {statusRows.length === 0 ? (
              <div className="dashboardEmptyLine">No orders in this period</div>
            ) : (
              <div className="dashboardStatusList">
                {statusRows.map((row) => {
                  const presentation =
                    STATUS_PRESENTATION[row.status] || {
                      label: row.status,
                      tone: "neutral",
                    };
                  return (
                    <div className="dashboardStatusRow" key={row.status}>
                      <StatusBadge
                        status={presentation.tone}
                        label={presentation.label}
                      />
                      <span className="dashboardStatusBar">
                        <span
                          className={`dashboardStatusBarFill ${presentation.tone}`}
                          style={{
                            width: `${statusMax ? (row.count / statusMax) * 100 : 0}%`,
                          }}
                        />
                      </span>
                      <span className="dashboardStatusCount">{row.count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>

        {/* TOP PRODUCTS + LOW STOCK */}
        <div className="dashboardGrid even">
          <Panel
            title={`Top ${productsWord.toLowerCase()}`}
            subtitle="By units sold in this period"
            flush
            footer={
              <Link className="dashboardPanelLink" to={productsPath}>
                View all {dashboard.productsCount} {productsWord.toLowerCase()} →
              </Link>
            }
          >
            {dashboard.topProducts.length === 0 ? (
              <div className="dashboardEmptyLine">
                Nothing sold in this period yet
              </div>
            ) : (
              <div className="dsPanelList">
                {dashboard.topProducts.map((product, index) => (
                  <div className="dsPanelListItem" key={product._id}>
                    <span className="dsPanelListRank">{index + 1}</span>
                    <span className="dsPanelListThumb">
                      {product.photoUrl ? (
                        <img src={`${graphqlUrl}${product.photoUrl}`} alt="" />
                      ) : (
                        <StoreIcon fontSize="small" />
                      )}
                    </span>
                    <span className="dsPanelListText">
                      <span className="dsPanelListTitle">
                        {product.title || "Untitled"}
                      </span>
                      <span className="dsPanelListMeta">
                        {product.quantity} sold
                      </span>
                    </span>
                    <span className="dsPanelListValue">
                      {formatMoney(product.revenue)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <Panel
            title="Needs restocking"
            subtitle={`${dashboard.outOfStockCount} out of stock, ${dashboard.lowStockCount} running low`}
            flush
            footer={
              <Link className="dashboardPanelLink" to={productsPath}>
                Manage inventory →
              </Link>
            }
          >
            {dashboard.lowStock.length === 0 ? (
              <div className="dashboardEmptyLine">
                Every {productWord} is well stocked
              </div>
            ) : (
              <div className="dsPanelList">
                {dashboard.lowStock.map((product) => (
                  <div className="dsPanelListItem" key={product._id}>
                    <span className="dsPanelListThumb">
                      {product.photoUrl ? (
                        <img src={`${graphqlUrl}${product.photoUrl}`} alt="" />
                      ) : (
                        <StoreIcon fontSize="small" />
                      )}
                    </span>
                    <span className="dsPanelListText">
                      <span className="dsPanelListTitle">
                        {product.title || "Untitled"}
                      </span>
                      <span className="dsPanelListMeta">
                        {formatMoney(product.price)}
                      </span>
                    </span>
                    <span
                      className={`dashboardStockValue ${
                        product.stock <= 0 ? "out" : "low"
                      }`}
                    >
                      {product.stock <= 0 ? "Out of stock" : `${product.stock} left`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
